import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';
import {
  OVERLAY_COLOR_HEX,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@bliks-io/ui';
import { formatNumber } from '@/lib/formatters';
import {
  CAPACITY_GREEN_MAX_SHARE,
  CAPACITY_YELLOW_MAX_SHARE,
  COMBINED_GREEN_MIN,
  COMBINED_YELLOW_MIN,
  RATING_GREEN_MIN,
  RATING_YELLOW_MIN,
  type HeatmapViewMode
} from '@/lib/processFlowHeatmap';

interface ProcessFlowHeatmapLegendProps {
  mode: Exclude<HeatmapViewMode, 'standard'>;
  maxHoursPerYear?: number;
}

const LEGEND_ROWS = [
  { color: 'green', bucket: 'good' },
  { color: 'yellow', bucket: 'medium' },
  { color: 'red', bucket: 'critical' },
  { color: 'gray', bucket: null }
] as const;

type LegendBucket = NonNullable<(typeof LEGEND_ROWS)[number]['bucket']>;

function bucketThresholds(
  mode: Exclude<HeatmapViewMode, 'standard'>,
  formatStars: (value: number) => string
): Record<LegendBucket, Record<string, string | number>> {
  if (mode === 'rating') {
    return {
      good: { min: formatStars(RATING_GREEN_MIN) },
      medium: { min: formatStars(RATING_YELLOW_MIN), max: formatStars(RATING_GREEN_MIN) },
      critical: { min: formatStars(RATING_YELLOW_MIN) }
    };
  }
  if (mode === 'capacity') {
    const greenMax = Math.round(CAPACITY_GREEN_MAX_SHARE * 100);
    const yellowMax = Math.round(CAPACITY_YELLOW_MAX_SHARE * 100);
    return {
      good: { max: greenMax },
      medium: { min: greenMax, max: yellowMax },
      critical: { min: yellowMax }
    };
  }
  const greenMin = Math.round(COMBINED_GREEN_MIN * 100);
  const yellowMin = Math.round(COMBINED_YELLOW_MIN * 100);
  return {
    good: { min: greenMin },
    medium: { min: yellowMin, max: greenMin },
    critical: { max: yellowMin }
  };
}

// Concrete hours behind the capacity percentage bands for THIS process, so the
// legend reads as "< 33% of max (< 30 h)" rather than an abstract share.
function bucketHours(
  maxHoursPerYear: number,
  formatHours: (value: number) => string
): Record<LegendBucket, string> {
  const greenMax = CAPACITY_GREEN_MAX_SHARE * maxHoursPerYear;
  const yellowMax = CAPACITY_YELLOW_MAX_SHARE * maxHoursPerYear;
  return {
    good: `< ${formatHours(greenMax)}`,
    medium: `${formatHours(greenMax)} – ${formatHours(yellowMax)}`,
    critical: `≥ ${formatHours(yellowMax)}`
  };
}

export function ProcessFlowHeatmapLegend({ mode, maxHoursPerYear }: ProcessFlowHeatmapLegendProps) {
  const { t, i18n } = useTranslation();
  const thresholds = bucketThresholds(mode, (value) =>
    value.toLocaleString(i18n.language, { minimumFractionDigits: 1 })
  );
  const capacityMaxHours = mode === 'capacity' ? (maxHoursPerYear ?? 0) : 0;
  const hoursByBucket =
    capacityMaxHours > 0
      ? bucketHours(
          capacityMaxHours,
          (value) =>
            `${formatNumber(Math.round(value), i18n.language)} ${t('processFlow.heatmapLegend.hoursUnit')}`
        )
      : null;

  return (
    <TooltipProvider>
      <div className="absolute top-4 left-4 z-10 rounded border border-line bg-white p-3 shadow-sm">
        <div className="mb-2 flex items-center gap-1.5">
          <p className="text-sm font-semibold text-foreground">
            {t(`processFlow.viewMode.${mode}`)}
          </p>
          {mode === 'combined' && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={t('processFlow.heatmapLegend.combinedInfo')}
                  className="text-fg-subtle hover:text-fg-secondary focus:outline-hidden focus-visible:ring-1 focus-visible:ring-ring rounded-sm"
                >
                  <Info className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs">
                {t('processFlow.heatmapLegend.combinedInfo')}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        <ul className="space-y-1">
          {LEGEND_ROWS.map(({ color, bucket }) => (
            <li key={color} className="flex items-center gap-2 text-sm text-fg-secondary">
              <span
                className="h-3.5 w-3.5 shrink-0 rounded-sm border"
                style={{
                  backgroundColor: OVERLAY_COLOR_HEX[color].fill,
                  borderColor: OVERLAY_COLOR_HEX[color].stroke
                }}
              />
              {bucket ? (
                <span className="flex flex-col leading-tight">
                  <span>
                    {t(`processFlow.heatmapLegend.${mode}.${bucket}`, thresholds[bucket])}
                  </span>
                  {hoursByBucket && (
                    <span className="text-xs text-fg-subtle tabular-nums">
                      {hoursByBucket[bucket]}
                    </span>
                  )}
                </span>
              ) : (
                t('processFlow.heatmapLegend.noData')
              )}
            </li>
          ))}
        </ul>
      </div>
    </TooltipProvider>
  );
}
